import { useEffect, useState } from 'react';
import { NAV_EVENT } from './nav.js';

/* ══════════════════════════════════════════════════════════════════════════
 * FROM THE WEBSITE TO THE APP — Al-Noor, 22 Oct 2026 ("once we set up the app
 * … push them towards downloading the app"), the norm he chose: Airbnb,
 * OpenTable, Uber Eats, Booking.
 *
 *   · a phone's browser: a slim "Get the Eleat app" bar (iPhone: Apple's own
 *     Smart App Banner, added by vite.config.js; Android: this bar), closable
 *   · the "Sign in or join Eleat" sheet: its main button becomes the app
 *   · after joining on the website: one screen, once
 *   · a computer: never pushed · inside the app: nothing
 *
 * ⚠️ ALL OF IT IS OFF UNTIL THE STORES SAY YES. Nothing draws while the store
 * links are empty (VITE_APP_STORE_URL / VITE_PLAY_STORE_URL, eleat-app's
 * Environment) — and Eleat is in no store until Al-Noor says so.
 * ══════════════════════════════════════════════════════════════════════════ */
const IOS = String(import.meta.env.VITE_APP_STORE_URL || '').trim();
const ANDROID = String(import.meta.env.VITE_PLAY_STORE_URL || '').trim();

/** running inside the store app (Capacitor), not a browser */
export function inApp() {
  try { return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()); } catch { return false; }
}

export function platform() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

/** the store page for this phone, or null (a computer, the app itself, no store yet) */
export function storeLink() {
  if (inApp()) return null;
  const p = platform();
  return p === 'ios' ? (IOS || null) : p === 'android' ? (ANDROID || null) : null;
}

const CLOSED = 'eleat.appbar.closed';
const WEEK = 7 * 24 * 60 * 60 * 1000;

/* Android's bar — iPhone has Apple's own banner, so it is never drawn twice */
export function AppBar() {
  const link = platform() === 'android' ? storeLink() : null;
  const [shut, setShut] = useState(() => {
    try { return Date.now() - Number(localStorage.getItem(CLOSED) || 0) < WEEK; } catch { return false; }
  });
  const on = !!link && !shut;
  /* the page moves down under the bar, never behind it */
  useEffect(() => {
    document.documentElement.classList.toggle('has-appbar', on);
    return () => document.documentElement.classList.remove('has-appbar');
  }, [on]);
  if (!on) return null;
  return (
    <div className="appbar" role="region" aria-label="Get the Eleat app">
      <img className="appbar-i" src="/icon-192.png" alt="" width="36" height="36" />
      <span className="appbar-t">Get the Eleat app</span>
      <a className="appbar-go" href={link} rel="noopener">Get</a>
      <button className="appbar-x" type="button" aria-label="Close"
        onClick={() => { try { localStorage.setItem(CLOSED, String(Date.now())); } catch { /* fine */ } setShut(true); }}>✕</button>
    </div>
  );
}

/* after joining on the website — once (Join.jsx sets the mark) */
const JUST = 'eleat.justJoined';
export function markJustJoined() { try { sessionStorage.setItem(JUST, '1'); } catch { /* fine */ } }

export function AfterJoinApp() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    /* joining moves in place (no reload), so it is checked on every screen
       change — and only once they have left the joined screen */
    const check = () => {
      try {
        if (/[?&]join=1/.test(window.location.search)) return;
        if (sessionStorage.getItem(JUST) && storeLink() && !localStorage.getItem('eleat.appAsked')) {
          sessionStorage.removeItem(JUST); localStorage.setItem('eleat.appAsked', '1'); setOpen(true);
        }
      } catch { /* private window: never asked */ }
    };
    check();
    window.addEventListener(NAV_EVENT, check);
    window.addEventListener('popstate', check);
    return () => { window.removeEventListener(NAV_EVENT, check); window.removeEventListener('popstate', check); };
  }, []);
  if (!open) return null;
  return (
    <div className="egate" role="dialog" aria-modal="true" aria-labelledby="appH">
      <div className="egate-veil" onClick={() => setOpen(false)} />
      <div className="egate-card">
        <div className="egate-grip" />
        <h3 className="egate-h" id="appH">Eleat works best in the app</h3>
        <a className="egate-go" href={storeLink()} rel="noopener">Get the Eleat app</a>
        <button className="egate-no" type="button" onClick={() => setOpen(false)}>Not now</button>
      </div>
    </div>
  );
}
